import React, { useState } from "react";
import {
  Drawer,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { IOSSwitch } from "./IOSSwitch";
import { fleetService } from "../../../api/fleetService/fleetServicesRegistered";
import { DriverDataPayload } from "../../../api/fleetService/fleetServicesRegistered";
import { useBusinessProfile } from "../../../utils/hooks/useBusinessProfile";
import { useAuth } from "../../../utils/hooks/useAuth";
import { driverDrawer } from "../style";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
// @ts-ignore
import 'react-phone-input-2/lib/material.css';

interface Driver {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  status: string;
  onboardingDateVerify: string;
  onboardingDateRequest: string;
  onboardingStatus: string;
  lastAccess: string;
  associations: any[];
}

interface SidebarAddDriverProps {
  open: boolean;
  onClose: () => void;
  onAddDriver: (newDriver: Driver) => void;
}

const SidebarAddDriver: React.FC<SidebarAddDriverProps> = ({
  open,
  onClose,
  onAddDriver,
}) => {
  const { businessProfile } = useBusinessProfile();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [newDriver, setNewDriver] = useState<Partial<Driver> & { phonePrefix?: string }>({
    firstName: "",
    lastName: "",
    phone: "",
    phonePrefix: "",
    email: "",
    status: "ACTIVE",
  });

  const [errors, setErrors] = useState<{ [key in keyof Partial<Driver>]?: string }>({});
  const [dataLoading, setDataLoading] = useState(false);

  const handleChange = (field: keyof Driver, value: string) => {
    setNewDriver((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async () => {
    let validationErrors: { [key in keyof Partial<Driver>]?: string } = {};
  
    if (!newDriver.firstName) validationErrors.firstName = "Il nome è obbligatorio";
    if (!newDriver.lastName) validationErrors.lastName = "Il cognome è obbligatorio";
  
    if (!newDriver.phone || newDriver.phone.trim() === "") {
      validationErrors.phone = "Il telefono è obbligatorio";
    } else {
      // Estrae solo i numeri dal telefono completo
      const phoneDigits = newDriver.phone.replace(/[^0-9]/g, "");
      const prefix = newDriver.phonePrefix || "";
      
      if (prefix && phoneDigits === prefix) {
        validationErrors.phone = "Inserisci anche il numero dopo il prefisso";
      }
    }
  
    if (!newDriver.email) {
      validationErrors.email = "L'email è obbligatoria";
    } else if (!validateEmail(newDriver.email)) {
      validationErrors.email = "Formato email non valido";
    }
  
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
  
    const driverPayload: DriverDataPayload = {
      legacyCodes: {
        legacyCode: businessProfile.legacycodes[0].legacycode,
        company: businessProfile.legacycodes[0].company,
      },
      firstName: newDriver.firstName || "",
      lastName: newDriver.lastName || "",
      phone: newDriver.phone?.startsWith("+") ? newDriver.phone : `+${newDriver.phone}` || "",
      email: newDriver.email || "",
      user: user?.email || "-",
      status: newDriver.status || "ACTIVE",
    };

  
    const fullDriver: Driver = {
      ...newDriver,
      phone: newDriver.phone?.startsWith("+") ? newDriver.phone : `+${newDriver.phone}`,
      onboardingDateVerify: "-",
      onboardingDateRequest: "-",
      onboardingStatus: "-",
      lastAccess: "-",
      associations: [],
    } as Driver;
    setDataLoading(true);
    try {
      const response = await fleetService.putDriver(driverPayload);
      const updatedDriver: Driver = {
        ...fullDriver,
        onboardingDateVerify: response.onboardingDateVerify,
        onboardingStatus: response.onboardingStatus,
      };
      console.log('updatedDriver',updatedDriver)
      onAddDriver(updatedDriver);
      setNewDriver({
        firstName: "",
        lastName: "",
        phone: "",
        phonePrefix: "",
        email: "",
        status: "ACTIVE",
      });
      onClose();
      toast.success("Driver aggiunto con successo!");
    } catch (error) {
      toast.error("Errore durante l'inserimento dell'autista!");
      console.error("Errore nell'aggiornamento dell'autista:", error);
    } finally {
      setDataLoading(false);
    }
  };
  

  const handleClose = () => {
    onClose();
    setNewDriver({
      firstName: "",
      lastName: "",
      phone: "",
      phonePrefix: "",
      email: "",
      status: "ACTIVE",
    });
    setErrors({});
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      elevation={5}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "transparent" },
        },
      }}
    >
      <Box sx={{ width: 520, position: "relative", height: "100%" }}>
        <Box
          sx={{
            padding: "32px 24px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: "700", color: "#333333" }}>
            {t("common.sidebars.addDriver.heading")}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon sx={{ color: "#333333" }} />
          </IconButton>
        </Box>

        <Divider />

        <Typography
          sx={{
            padding: "0 24px",
            paddingTop: "16px",
            fontSize: "18px",
            fontWeight: "700",
            color: "#333333",
          }}
        >
          {t("common.sidebars.addDriver.paragraph")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: "16px 24px",
            rowGap: "16px",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "row", columnGap: "16px", width: "100%" }}>
            <Box sx={driverDrawer.generalBox}>
              <Typography sx={driverDrawer.mainText}>
                {t("common.sidebars.addDriver.name")} *
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={newDriver.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
                error={!!errors.firstName}
                helperText={errors.firstName}
                placeholder={t("common.sidebars.addDriver.name") || ""}
              />
            </Box>
            <Box sx={driverDrawer.generalBox}>
              <Typography sx={driverDrawer.mainText}>
                {t("common.sidebars.addDriver.surname")} *
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={newDriver.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
                error={!!errors.lastName}
                helperText={errors.lastName}
                placeholder={t("common.sidebars.addDriver.surname") || ""}
              />
            </Box>
          </Box>

          <Box sx={driverDrawer.generalBox}>
            <Typography sx={driverDrawer.mainText}>
              {t("common.sidebars.addDriver.phone")} *
            </Typography>
            <Box
  sx={{
    '& .flag-dropdown': {
      width:50,
      height:54,
      backgroundColor: '#f5f5f5',
      borderRight: '1px solid #ccc',
       borderRadius:'4px 0px 0px 4px',

    left:1  ,
  top:1  },
  }}
>
  <PhoneInput
    country={'it'}
    value={newDriver.phone}
    onChange={(value: string, data: any) => {
      setNewDriver((prev) => ({
        ...prev,
        phone: value,
      }));
      setErrors((prev) => ({ ...prev, phone: "" }));
    }}
    inputStyle={{
      width: "100%",
      height: "56px",
      fontSize: "16px",
      paddingLeft: "60px",
      borderColor: errors.phone ? "#d32f2f" : undefined,

    }}
    containerClass="react-tel-input"
    inputProps={{
      name: "phone",
      required: true,
    }}
    enableLongNumbers
    disableCountryCode={false}
    disableDropdown={false}
    countryCodeEditable={false}
    containerStyle={{ width: "100%" }}
    specialLabel=""
  />
</Box>

{errors.phone && (
  <Typography variant="caption" color="error">
    {errors.phone}
  </Typography>
)}

          </Box>

          <Box sx={driverDrawer.generalBox}>
            <Typography sx={driverDrawer.mainText}>
              {t("common.sidebars.addDriver.email")} *
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={newDriver.email}
              onChange={(e) => handleChange("email", e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              placeholder={t("common.sidebars.addDriver.email") || ""}
            />
          </Box>

          <Box sx={driverDrawer.statusBox}>
            <Typography sx={driverDrawer.mainText}>
              {t("common.sidebars.addDriver.driverStatus")}:
            </Typography>
            <IOSSwitch
              checked={newDriver.status === "ACTIVE"}
              onChange={(e) =>
                setNewDriver({
                  ...newDriver,
                  status: e.target.checked ? "ACTIVE" : "CANCELLED",
                })
              }
            />
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            width: "100%",
            backgroundColor: "white",
            zIndex: 200,
            height: "93px",
          }}
        >
          <Button
            variant="contained"
            fullWidth
            disabled={dataLoading}
            onClick={handleSubmit}
            sx={{
              mt: 2,
              position: "absolute",
              bottom: "24px",
              maxWidth: "124px",
              right: "24px",
              backgroundColor: "#CF0A2C",
            }}
          >
            {t("common.sidebars.addDriver.add")}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default SidebarAddDriver;
